// arbeitgeberVerbindung.ts — der Arbeitgeber erscheint sofort unter
// „Verbindung zu Unternehmen" (User 30.09.2026).
//
// Der Arbeitgeber wird im Beruf-Block gewählt (`employerContactId`) und über
// `PUT contact/:id/arbeitgeber` gespeichert; das legt serverseitig die
// Beziehung „Mensch im Unternehmen" an. In der Liste `rel_personFirma` stand
// die Firma aber erst nach dem Speichern und Neuladen. Jetzt spiegelt die
// Maske sie sofort als Eintrag ohne Rolle, mit dem Haken „Mensch im
// Unternehmen".
//
// Regeln:
//   - Die Firma kommt nur dazu, wenn sie noch nicht in der Liste steht.
//   - Der Eintrag trägt `autoAusArbeitgeber`. Wird der Arbeitgeber gelöst oder
//     gewechselt, fällt er wieder weg — aber nur, solange er unverändert ist.
//     Was der Anwender daran gepflegt hat (Rolle, ausgeschieden …), bleibt
//     als gewöhnlicher Eintrag stehen.
//   - Ein neu eingetippter Arbeitgeber (Schnellanlage) hat noch keine Id: Sein
//     Eintrag zieht den Namen mit, und angelegt wird die Firma NUR über den
//     Arbeitgeber-Weg (persistRelations überspringt ihn), sonst gäbe es sie
//     doppelt.
//   - Gespeichert wird nichts über diesen Eintrag: Ohne Rolle erzeugt
//     `relationBodies` keine Zeile, und `PUT arbeitgeber` markiert bzw. legt
//     die Zeile „Mensch im Unternehmen" an.

import type { ContactRef, PersonFirmaItem } from "../components/relationsInline/types";
import { uid } from "../components/relationsInline/types";

/** Der gespiegelte Eintrag für eine Arbeitgeber-Firma. */
export function arbeitgeberEintrag(ref: ContactRef): PersonFirmaItem {
  return {
    id: uid(),
    ref: { ...ref, type: "Firma" },
    verant: {
      isExtern: false,
      roles: [],
      externalCompany: null,
      istMenschImUnternehmen: true,
      // Ohne Rolle leitet die Verantwortlichkeit den Haken als „aus" ab — wie
      // beim geladenen Bestand (eigene Zeile „Mensch im Unternehmen") gilt er
      // deshalb als bewusst gesetzt.
      istMenschImUnternehmenManuell: true,
    },
    autoAusArbeitgeber: true,
  };
}

/** Automatischer Eintrag, den seit dem Spiegeln niemand verändert hat? */
export function istUnveraenderterAutoEintrag(it: PersonFirmaItem): boolean {
  if (!it?.autoAusArbeitgeber) return false;
  const v = it.verant;
  return (
    !!v &&
    !v.isExtern &&
    (v.roles ?? []).length === 0 &&
    !v.externalCompany &&
    !v.assistenz &&
    v.istMenschImUnternehmen === true &&
    !it.ausgeschieden &&
    !(it.beginn ?? "").trim() &&
    !(it.ende ?? "").trim()
  );
}

const firmaIdVon = (it: PersonFirmaItem): number | undefined => {
  const firma = it.verant?.isExtern ? it.verant?.externalCompany : it.ref;
  const id = Number(firma?.contactId);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

/**
 * Die Liste `rel_personFirma` nach einem Arbeitgeberwechsel.
 *
 * @param items  die bisherige Liste
 * @param arbeitgeber  der neue Arbeitgeber — bestehend (`contactId`) oder neu
 *   eingetippt (ohne Id); `null` = gelöst
 * @returns die neue Liste; dieselbe Referenz, wenn sich nichts ändert
 */
export function arbeitgeberSpiegeln(
  items: PersonFirmaItem[],
  arbeitgeber: ContactRef | null,
): PersonFirmaItem[] {
  const neuId = Number(arbeitgeber?.contactId) > 0 ? Number(arbeitgeber!.contactId) : undefined;
  const neuName = (arbeitgeber?.lastName || arbeitgeber?.displayName || "").trim();
  const hatNeuen = !!arbeitgeber && (neuId !== undefined || !!neuName);

  let geaendert = false;
  let next: PersonFirmaItem[] = [];
  let neuerSchonDa = false;

  for (const it of items) {
    if (it?.autoAusArbeitgeber) {
      const id = firmaIdVon(it);
      // Neu eingetippter Arbeitgeber: sein (einziger) Eintrag ohne Id zieht
      // Name und Angaben mit — auch wenn der Anwender ihn schon verändert hat.
      if (hatNeuen && neuId === undefined && id === undefined && !neuerSchonDa) {
        neuerSchonDa = true;
        const ref: ContactRef = { ...(it.ref ?? {}), ...arbeitgeber!, type: "Firma" };
        if (JSON.stringify(ref) !== JSON.stringify(it.ref)) {
          next.push({ ...it, ref });
          geaendert = true;
        } else {
          next.push(it);
        }
        continue;
      }
      if (hatNeuen && neuId !== undefined && id === neuId) {
        neuerSchonDa = true;
        next.push(it);
        continue;
      }
      // Gehört zu einem früheren Arbeitgeber: weg, solange unverändert.
      if (istUnveraenderterAutoEintrag(it)) {
        geaendert = true;
        continue;
      }
      // Verändert → bleibt, aber als gewöhnlicher Eintrag: Er hängt an keinem
      // Arbeitgeber mehr, und ein neu eingetippter wird dann über die Liste
      // angelegt (sonst ginge er samt Rolle verloren).
      const { autoAusArbeitgeber: _weg, ...rest } = it;
      next.push(rest);
      geaendert = true;
      continue;
    }
    if (neuId !== undefined && firmaIdVon(it) === neuId) neuerSchonDa = true;
    next.push(it);
  }

  if (hatNeuen && !neuerSchonDa) {
    next = [...next, arbeitgeberEintrag(arbeitgeber!)];
    geaendert = true;
  }
  return geaendert ? next : items;
}
